import { useState, useEffect, useCallback, useMemo } from 'react'
// 다른 소스코드 영역(Explore / YamlEditor / OutputTabs 등)과 동일한 폰트/스타일/툴바 통일
// — 직접 @uiw/react-codemirror 가 아닌 공용 CodeMirrorWithWrap 사용.
import CodeMirror from './CodeMirrorWithWrap'
import { yaml as yamlLang } from '@codemirror/lang-yaml'
import { oneDark } from '@codemirror/theme-one-dark'
import { api } from '../../api'
import { APP_DEFAULTS } from '../../defaults'

/**
 * 기존 SV 추천 모달 (KBNL-74)
 *
 * 4개 화면(SqlConverter / SqlConverterWorkbench / App.jsx SV 탭 / CompositeViewer)
 * 에서 공용으로 사용하는 추천 다이얼로그.
 *
 * 단일 선택(single navigation) 전용 — 행 클릭은 "선택"이고, 푸터의
 * "이동하기 →" 버튼을 눌러야 onSelect(candidate) 호출 후 모달이 닫힌다.
 * 행 선택 시 우측(또는 하단) 미리보기 영역에 description + 본체 YAML
 * 일부가 표시되어, 이동 전에 어떤 SV 인지 확인할 수 있다.
 * (이전엔 multi 모드로 reference_views 동봉용 체크박스가 있었으나,
 * 사용자 피드백상 "유사 SV 화면으로 이동"의 의미가 더 명확해 단순화함 — KBNL-75 후속.)
 *
 * 모드:
 *   - 'sql'      : sqlText 를 백엔드에 보냄.
 *   - 'seed_sv'  : seedSvName 의 자연어 + source 테이블 기반 검색.
 *                  기본은 자기 자신 제외(exclude_name=seedSvName).
 *   - 'query'    : 모달 안에서 사용자가 입력한 자연어 (수동 검색).
 *
 * 모드와 무관하게 모달 안에서 사용자가 query 텍스트를 직접 수정해 재검색할 수 있다.
 *
 * Props:
 *   open            : boolean
 *   onClose         : () => void
 *   mode            : 'sql' | 'seed_sv' | 'query'
 *   sqlText         : string  (mode='sql' 일 때 사용)
 *   seedSvName      : string  (mode='seed_sv' 일 때 사용)
 *   seedSvScope     : string  (선택, seed scope 힌트)
 *   initialQueryText: string  (선택, 모달 열릴 때 검색바를 자동 prefill)
 *   storageSettings : object  (백엔드 호출용)
 *   showToast       : (level, msg) => void
 *   onSelect        : (candidate) => void   행 클릭 시 호출됨 (모달은 자동 닫힘)
 *   title           : string (선택, 모달 헤더)
 *   topN            : number (기본 10)
 */
export default function SvRecommendDialog({
  open,
  onClose,
  mode = 'sql',
  sqlText = '',
  seedSvName = '',
  seedSvScope = '',
  // KBNL-74 후속: 편집 중 (저장 안 한) 상태로 검색하기 위한 override.
  // SV 탭이 YamlEditor 의 현재 값을 전달 → backend 가 Firestore 대신 사용.
  // 비어있으면 backend 가 기존대로 Firestore 에서 doc 로드.
  seedSvOverrideYaml = '',
  seedSvOverrideDescription = '',
  initialQueryText = '',
  storageSettings,
  showToast,
  onSelect,
  title = '유사 Semantic View 찾기',
  topN = 10,
  editorTheme,   // 호출부에서 전체 앱과 동일한 테마(light/dark) 전달 — 미지정 시 oneDark
}) {
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(null)            // { candidates, user_tables, diagnostics }
  const [queryOverride, setQueryOverride] = useState('') // 사용자가 모달 안에서 직접 입력한 검색어
  // 행 클릭 시 선택되는 후보 — "이동하기" 버튼 활성화 + 미리보기 영역에 표시
  const [selectedCandidate, setSelectedCandidate] = useState(null)

  // 모달 열릴 때마다 상태 초기화 + 자동 검색.
  // - seed_sv 모드: backend 의 /sv/extracted-keywords 호출 → vector 채널이
  //   실제 사용하는 텍스트(name + description + dim/measure display_name/comment/synonyms)
  //   를 받아와 검색바에 prefill. 사용자에게 보이는 텍스트 = backend 가 임베딩에
  //   쓰는 텍스트 (단일 진실원). 사용자가 편집하면 그 편집본으로 재검색.
  // - 그 외 모드 (sql / query / CompositeViewer 카드 등): initialQueryText 가
  //   주어지면 그것으로 prefill, 없으면 빈 상태로 시작.
  useEffect(() => {
    if (!open) return
    setResult(null)
    setSelectedCandidate(null)

    // seed_sv 모드 → backend 추출본을 prefill 로 사용
    if (mode === 'seed_sv' && seedSvName) {
      // 우선 빈 값으로 시작 (loading UX) — 추출 완료 후 채움
      setQueryOverride('')
      setLoading(true)
      api.extractSvKeywords({
        seed_sv_name: seedSvName,
        seed_sv_scope: seedSvScope || '',
        // KBNL-74 후속: 편집 중 YAML/desc 가 있으면 함께 전송 → backend 가
        // Firestore 의 저장본 대신 이걸 기준으로 추출.
        override_yaml: seedSvOverrideYaml || '',
        override_description: seedSvOverrideDescription || '',
        storage_type: storageSettings?.storage_type || APP_DEFAULTS.storage_type,
        gcs_path: storageSettings?.gcs_path || '',
        fs_project: storageSettings?.fs_project || '',
        fs_database: storageSettings?.fs_database || '',
        fs_collection: storageSettings?.fs_collection || '',
        user_id: storageSettings?.user_id || '',
      }).then(res => {
        // KBNL-74 후속: SV name 은 prefill 에 안 넣음 — 유일 식별자라 매칭 신호 없음.
        // backend 가 빈 문자열 반환 시(seed_doc 없음 등) 검색바도 빈 채로 두고
        // seed_sv_name 만으로 keyword + identifier 채널 매칭 시도.
        // 추출 텍스트는 줄바꿈(\n)으로 이어져 오는데 한 줄 input 은 줄바꿈을
        // 표시 못 해 항목이 붙어 보인다 → 공백으로 치환해 넣는다 (검색 의미 동일).
        const extracted = (res?.extracted_text || '').trim().replace(/\s*\n+\s*/g, ' ')
        setQueryOverride(extracted)
        // 랭킹 정확도: 초기(자동) 검색은 query_text 를 보내지 않고 seed 모드
        // 그대로 — 후보 문서들이 색인된 것과 동일한 기준(seed 의 저장 임베딩
        // 텍스트, 이름 포함)으로 비교한다. prefill 텍스트(이름 제외)를
        // query_text 로 보내면 벡터 기준이 달라져, 도구 태그('(SQL Workbench)'
        // 등) 가 같은 문서가 진짜 사본보다 위로 오는 왜곡이 있었다.
        // 사용자가 검색바를 수정하고 [검색] 하면 그때 query 모드로 전환.
        runSearch('')
      }).catch(e => {
        // 추출 endpoint 실패 — 검색바 빈 채로 seed_sv_name 만으로 검색
        // (vector 채널은 seed_doc 의 stored embedding 으로 fallback)
        showToast?.('warning', `키워드 추출 실패 (검색바 비움): ${e.message}`)
        setQueryOverride('')
        runSearch('')
      })
      return
    }

    // 그 외 모드 → initialQueryText 사용
    const seed = (initialQueryText || '').trim()
    setQueryOverride(seed)
    runSearch(seed)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, sqlText, seedSvName, seedSvScope, seedSvOverrideYaml, seedSvOverrideDescription, initialQueryText])

  // ESC 닫기
  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  /**
   * 백엔드 추천 호출.
   *
   * mode 와 overrideText 의 조합:
   * - seed_sv : seed_sv_name 항상 보냄 (keyword 채널은 SV YAML 의 테이블 사용).
   *             overrideText 가 있으면 query_text 도 함께 보냄 (vector 채널은
   *             query_text 를 우선 사용 — backend 의 vector channel 기본 동작).
   * - sql     : overrideText 가 있으면 query_text, 없으면 sql_text.
   * - query   : overrideText 가 있어야 검색.
   */
  const runSearch = useCallback(async (overrideText) => {
    setLoading(true)
    try {
      const body = {
        top_n: topN,
        storage_type: storageSettings?.storage_type || APP_DEFAULTS.storage_type,
        gcs_path: storageSettings?.gcs_path || '',
        fs_project: storageSettings?.fs_project || '',
        fs_database: storageSettings?.fs_database || '',
        fs_collection: storageSettings?.fs_collection || '',
        user_id: storageSettings?.user_id || '',
      }
      const trimmedOverride = (overrideText || '').trim()

      // seed_sv 는 query_text 와 공존 (둘 다 보냄). 다른 모드는 mutex.
      if (mode === 'seed_sv' && seedSvName) {
        body.seed_sv_name = seedSvName
        body.seed_sv_scope = seedSvScope || ''
        body.exclude_name = seedSvName
        // KBNL-74 후속: 편집 중 상태 override (있으면 backend 가 Firestore 무시)
        if (seedSvOverrideYaml) {
          body.override_yaml = seedSvOverrideYaml
          body.override_description = seedSvOverrideDescription || ''
        }
        if (trimmedOverride) body.query_text = trimmedOverride
      } else if (trimmedOverride) {
        body.query_text = trimmedOverride
      } else if (mode === 'sql' && sqlText) {
        body.sql_text = sqlText
      } else if (mode === 'query') {
        // mode=query 인데 입력 없음 → 사용자 입력 대기
        setResult(null)
        setLoading(false)
        return
      } else {
        // 입력 없음 — 빈 결과
        setResult({ candidates: [], user_tables: [], diagnostics: {} })
        setLoading(false)
        return
      }
      const res = await api.recommendReferenceViews(body)
      setResult(res)
      // 새 검색 결과가 오면 이전 선택은 무효 — 미리보기/이동하기 버튼 reset
      setSelectedCandidate(null)
    } catch (e) {
      showToast?.('error', `추천 실패: ${e.message}`)
      setResult({ candidates: [], user_tables: [], diagnostics: {} })
      setSelectedCandidate(null)
    } finally {
      setLoading(false)
    }
  }, [mode, sqlText, seedSvName, seedSvScope, seedSvOverrideYaml, seedSvOverrideDescription, storageSettings, topN, showToast])

  const handleManualSearch = useCallback(() => {
    const q = queryOverride.trim()
    if (!q) {
      showToast?.('warning', '검색어를 입력하세요.')
      return
    }
    runSearch(q)
  }, [queryOverride, runSearch, showToast])

  // 행 클릭 — 선택만 하고 이동은 푸터 버튼으로 (실수 방지 + 미리보기 확인)
  // 같은 행 다시 클릭하면 선택 해제
  const handleRowClick = useCallback((c) => {
    setSelectedCandidate(prev =>
      (prev && prev.scope === c.scope && prev.name === c.name) ? null : c
    )
  }, [])

  // "이동하기" 버튼 — 실제 navigation
  const handleNavigate = useCallback(() => {
    if (!selectedCandidate) return
    // 코드 리뷰: _shared scope 후보는 owner_user_id 가 빈 값이라 호출 화면이
    // scope 를 잃는다 (같은 이름의 user:* SV 가 먼저 열리는 오동작). sentinel
    // '_shared' 를 owner 로 채워 전달 — 단건 조회 API 가 _shared 를 직접 연다.
    const cand = (selectedCandidate.scope === '_shared' && !selectedCandidate.owner_user_id)
      ? { ...selectedCandidate, owner_user_id: '_shared' }
      : selectedCandidate
    onSelect?.(cand)
    onClose()
  }, [selectedCandidate, onSelect, onClose])

  // 선택된 후보의 본체 YAML 우선순위 결정
  // 우선순위:
  //   1. composite_yaml — Composite View
  //   2. metric_view_yaml — Metric View (Generator 산출물)
  //   3. semantic_yaml — 일반 Semantic View (SqlConverter/Workbench 산출물 등)
  //   4. yaml_string — legacy
  // 주의: Workbench 에서 저장된 SV 는 metric_view_yaml 에 '# -- description --'
  // 주석 블록만 들어 있고 실제 본문은 semantic_yaml 에 있다. 값이 '있냐'가 아니라
  // '주석 제외 실질 본문이 있냐'로 판단해야 실제 내용과 다른 미리보기가 안 나온다.
  const previewYaml = useMemo(() => {
    if (!selectedCandidate) return ''
    const bodies = [
      selectedCandidate.composite_yaml,
      selectedCandidate.metric_view_yaml,
      selectedCandidate.semantic_yaml,
      selectedCandidate.yaml_string,
    ]
    // 주석(#)/빈 줄만으로 이루어진 YAML 은 실질 본문이 아님 → 다음 후보로 fallthrough
    const hasRealBody = (s) =>
      !!s && s.split('\n').some(line => {
        const t = line.trim()
        return t && !t.startsWith('#')
      })
    return bodies.find(hasRealBody) || bodies.find(s => s && s.trim()) || ''
  }, [selectedCandidate])

  if (!open) return null

  const candidates = result?.candidates || []
  const diag = result?.diagnostics || {}

  // 모드별 부제목 — 무엇을 기준으로 검색했는지 짧게. 검색어 전체는 아래
  // 검색 입력창에 그대로 보이므로 여기서 반복하지 않는다 (중복 제거).
  let subTitle = ''
  if (mode === 'sql' && sqlText) {
    subTitle = `SQL 기반 (테이블 ${(result?.user_tables || []).length}개)`
  } else if (mode === 'query' && !queryOverride.trim()) {
    subTitle = '검색어를 입력하세요'
  }
  // seed_sv 모드는 제목(title)에 이미 "'X' 와 유사한 SV" 가 있어 부제 생략.

  return (
    <div className="sv-recommend-overlay" onClick={() => !loading && onClose()}>
      <div className="sv-recommend-modal" onClick={e => e.stopPropagation()}>
        {/* ── 헤더 ───────────────────────────────────────────── */}
        <div className="sv-recommend-modal-head">
          <div>
            <h3>{title}</h3>
            {subTitle && <div className="sv-recommend-modal-sub">{subTitle}</div>}
          </div>
          <button
            className="btn-link"
            onClick={onClose}
            title="닫기 (Esc)"
            style={{ marginLeft: 'auto', fontSize: 14 }}
          >✕</button>
        </div>

        {/* ── 검색바 (수동 query 입력) ──────────────────────── */}
        <div className="sv-recommend-modal-search">
          <label className="sv-recommend-search-label" htmlFor="sv-recommend-query">검색어</label>
          <input
            id="sv-recommend-query"
            type="text"
            className="field"
            placeholder="자연어 검색 (예: 고객 거래 분석, 카드 매출 집계...) — 비우면 위 기본 입력 기반"
            value={queryOverride}
            onChange={e => setQueryOverride(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleManualSearch() } }}
            disabled={loading}
            style={{ flex: 1 }}
          />
          <button className="btn-secondary" onClick={handleManualSearch} disabled={loading}>
            {loading ? '검색 중...' : '검색'}
          </button>
          <button
            className="btn-link"
            title="기본 입력으로 재검색"
            onClick={() => { setQueryOverride(''); runSearch('') }}
            disabled={loading}
            style={{ fontSize: 12 }}
          >초기화</button>
        </div>

        {/* ── 본문: 2단 (좌: 후보 목록 / 우: 코드 미리보기) ──────
             미리보기가 목록 아래에 있으면 선택 시 목록을 가리므로
             (사용자 피드백) 좌우 분할. 우측 칼럼은 항상 렌더 —
             선택 전에는 placeholder 를 보여줘 레이아웃이 안 흔들린다. */}
        <div className="sv-recommend-modal-columns">
        <div className="sv-recommend-modal-body">
          {loading && (
            <div className="sv-recommend-empty">검색 중...</div>
          )}
          {!loading && result && (
            <>
              <div className="sv-recommend-head">
                {/* 개발자용 진단 수치(건수·ms·mode)는 tooltip 으로만 — 화면엔
                    사람이 읽을 것만. vector 사용 불가일 때만 사용자용 안내 노출. */}
                <span
                  style={{ fontWeight: 600 }}
                  title={
                    `의미 ${diag.vector_results || 0} · 테이블 ${diag.keyword_results || 0}` +
                    (diag.identifier_enabled ? ` · 컬럼 ${diag.identifier_results || 0}` : '') +
                    ` · ${diag.embedding_ms || 0}/${diag.vector_query_ms || 0}/${diag.keyword_scan_ms || 0}ms` +
                    (diag.mode ? ` · mode=${diag.mode}` : '')
                  }
                >
                  추천 후보 {candidates.length}개
                </span>
                {diag.vector_enabled === false && (
                  <span style={{ color: 'var(--text-sub)', fontSize: 11, marginLeft: 8 }}>
                    · 키워드(테이블·컬럼) 검색만 사용 중
                  </span>
                )}
              </div>
              {/* 임베딩 사용 불가(조직 정책 차단 등) — 키워드 매치만 사용됨 안내 */}
              {diag.vector_unavailable_reason && (
                <div style={{
                  margin: '6px 0', padding: '6px 10px', fontSize: 12, borderRadius: 6,
                  color: '#b45309', background: 'rgba(245,158,11,0.10)', border: '1px solid #f59e0b',
                }}>
                  ⚠ 임베딩(의미 검색)을 사용할 수 없어 <strong>키워드/식별자 매치만</strong> 사용되었습니다
                  — {diag.vector_unavailable_reason.slice(0, 160)}
                </div>
              )}
              {/* 무엇을 기준으로 찾았는지 — 테이블/검색 텍스트/컬럼 3줄.
                  라벨은 한국어로, 값은 전체 노출 (사용자가 근거를 확인). */}
              {((result.user_tables || []).length > 0 || diag.embed_input_preview || diag.seed_identifier_count > 0) && (
                <div className="sv-recommend-extracted">
                  {(result.user_tables || []).length > 0 && (
                    <div>
                      <span className="sv-recommend-extracted-label">테이블:</span>{' '}
                      <span className="sv-recommend-extracted-tables">
                        {result.user_tables.join(', ')}
                      </span>
                    </div>
                  )}
                  {diag.embed_input_preview && (
                    <div title={diag.embed_input_preview}>
                      <span className="sv-recommend-extracted-label">검색 텍스트:</span>{' '}
                      <span className="sv-recommend-extracted-text">
                        {diag.embed_input_preview.length > 160
                          ? diag.embed_input_preview.slice(0, 160) + '…'
                          : diag.embed_input_preview}
                      </span>
                    </div>
                  )}
                  {diag.identifier_enabled && diag.seed_identifier_count > 0 && (
                    <div>
                      <span className="sv-recommend-extracted-label">컬럼:</span>{' '}
                      <span className="sv-recommend-extracted-text">
                        {diag.seed_identifier_count}개
                      </span>
                    </div>
                  )}
                </div>
              )}
              {candidates.length === 0 ? (
                <div className="sv-recommend-empty">
                  매칭되는 기존 SV 가 없습니다.
                </div>
              ) : (
                <>
                {/* 범례 — 2줄 카드의 매칭 칩(v/k/i) 약자 설명 */}
                <div className="sv-recommend-legend">
                  <strong>매칭</strong> 표시 — <b>v</b>: 의미 · <b>k</b>: 테이블 · <b>i</b>: 컬럼
                  <span className="sv-recommend-legend-dim"> (숫자=순위, %=겹침 비율)</span>
                </div>
                <ul className="sv-recommend-list">
                  {candidates.map((c) => {
                    const isSelected =
                      selectedCandidate &&
                      selectedCandidate.scope === c.scope &&
                      selectedCandidate.name === c.name
                    // owner 표시 — 좌측 Sidebar.jsx 와 동일 패턴 (display_name 우선,
                    // 없으면 사번). 마우스 오버 시 사번 등 상세는 tooltip 으로.
                    const ownerName = c.owner_name || c.owner_user_id || ''
                    const isMine = c.is_mine !== false  // 메타가 없으면 본인 것으로 간주
                    const ownerTip = ownerName
                      ? [
                          `작성자: ${ownerName}`,
                          c.owner_user_id ? `사번: ${c.owner_user_id}` : null,
                        ].filter(Boolean).join('\n')
                      : undefined
                    return (
                    <li
                      key={`${c.scope}|${c.name}`}
                      className={
                        `sv-recommend-row sv-recommend-clickable` +
                        (c.weak ? ' sv-recommend-weak' : '') +
                        (isSelected ? ' sv-recommend-selected' : '')
                      }
                      title={c.description || '클릭하면 미리보기. 푸터의 "이동하기" 버튼으로 SV 화면으로 이동합니다.'}
                      onClick={() => handleRowClick(c)}
                    >
                      {/* 항목당 2줄 카드:
                          1줄 — 이름(굵게) + 매칭 칩(v/k/i) + 우측 작성자
                          2줄 — 테이블 + 컬럼 (메타) */}
                      <div className="sv-recommend-row-top">
                        <span className="sv-recommend-name">{c.name}</span>
                        <span className="sv-recommend-cell-match">
                          {c.rank_vector != null && (
                            <span className="sv-recommend-chip" title="의미 유사도(vector) 순위">v#{c.rank_vector}</span>
                          )}
                          {c.rank_keyword != null && (
                            <span className="sv-recommend-chip" title="테이블 겹침 비율">
                              k#{c.rank_keyword} ({Math.round((c.table_overlap_ratio || 0) * 100)}%)
                            </span>
                          )}
                          {c.rank_identifier != null && (
                            <span className="sv-recommend-chip" title="컬럼 겹침 비율">
                              i#{c.rank_identifier} ({Math.round((c.identifier_overlap_ratio || 0) * 100)}%)
                            </span>
                          )}
                        </span>
                        {ownerName ? (
                          <span className="std-list-owner sv-recommend-owner" title={ownerTip}>
                            {ownerName}
                            {isMine && <span className="std-list-user-badge">나</span>}
                          </span>
                        ) : (
                          <span className="std-list-owner sv-recommend-owner">_shared</span>
                        )}
                      </div>
                      {((c.matched_tables || []).length > 0 || (c.matched_identifiers || []).length > 0) && (
                        <div className="sv-recommend-row-meta">
                          {(c.matched_tables || []).length > 0 && (
                            <span className="sv-recommend-tables" title={c.matched_tables.join(', ')}>
                              📋 {c.matched_tables.slice(0, 2).join(', ')}{c.matched_tables.length > 2 ? ` 외 ${c.matched_tables.length - 2}` : ''}
                            </span>
                          )}
                          {(c.matched_identifiers || []).length > 0 && (
                            <span className="sv-recommend-tables" title={`겹치는 컬럼: ${c.matched_identifiers.join(', ')}`}>
                              🆔 {c.matched_identifiers.slice(0, 2).join(', ')}{c.matched_identifiers.length > 2 ? ` 외 ${c.matched_identifiers.length - 2}` : ''}
                            </span>
                          )}
                        </div>
                      )}
                    </li>
                    )
                  })}
                </ul>
                </>
              )}
            </>
          )}
        </div>

        {/* ── 미리보기 (우측 칼럼) ─────────────────────────────
             선택된 후보의 description + 본체 YAML 을 표시.
             이동 전에 어떤 SV 인지 사용자가 확인할 수 있게 한다. */}
        <div className="sv-recommend-modal-preview">
          {selectedCandidate ? (
            <>
              <div className="sv-recommend-preview-head">
                <span style={{ fontWeight: 600 }}>{selectedCandidate.name}</span>
                <span style={{ color: 'var(--text-sub)', fontSize: 11, marginLeft: 8 }}>
                  {selectedCandidate.is_mine ? '내 SV' :
                    (selectedCandidate.owner_user_id ? `@${selectedCandidate.owner_user_id}` : '_shared')}
                </span>
              </div>
              {selectedCandidate.description && (
                <div className="sv-recommend-preview-desc">
                  {selectedCandidate.description}
                </div>
              )}
              {previewYaml && (
                <div className="sv-recommend-preview-yaml" title="본체 YAML — 이동 후 전체 편집 가능">
                  <CodeMirror
                    value={previewYaml.length > 4000
                      ? previewYaml.slice(0, 4000) + '\n# ... (이하 생략 — 이동 후 전체 보기)'
                      : previewYaml}
                    height="100%"
                    theme={editorTheme || oneDark}
                    extensions={[yamlLang()]}
                    editable={false}
                    readOnly
                    wrapDefault
                    basicSetup={{
                      lineNumbers: true,
                      foldGutter: false,
                      highlightActiveLine: false,
                      highlightActiveLineGutter: false,
                    }}
                  />
                </div>
              )}
              {!previewYaml && !selectedCandidate.description && (
                <div className="sv-recommend-preview-empty">
                  미리보기 가능한 본문이 없습니다.
                </div>
              )}
            </>
          ) : (
            <div className="sv-recommend-preview-empty">
              왼쪽 목록에서 후보를 선택하면 내용 미리보기가 표시됩니다.
            </div>
          )}
        </div>
        </div>{/* /sv-recommend-modal-columns */}

        {/* ── 푸터 ───────────────────────────────────────────── */}
        <div className="sv-recommend-modal-foot">
          <button className="btn-secondary" onClick={onClose}>닫기</button>
          <button
            className="btn-primary"
            onClick={handleNavigate}
            disabled={!selectedCandidate}
            title={selectedCandidate
              ? `'${selectedCandidate.name}' 화면으로 이동`
              : '먼저 후보를 선택하세요'}
          >
            이동하기 →
          </button>
        </div>
      </div>
    </div>
  )
}
